import { FC, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppDispatch, useAppSelector, useScrollToTop } from "hooks/hooks";
import { AuthContext } from "context/appContext";
import { useNavigate } from "react-router-dom";
import { confirmReservation, fetchPendingOwnerReservation } from "redux/features/reservations/reservationSlice";
import { socket } from "utils/socket";
import ModernPendingReservationsHeader from "./ModernPendingReservationsHeader";
import ModernReservationRequestCard from "./ModernReservationRequestCard";
import Pagination from "shared/Pagination/Pagination";
import AlertModalDelete from "containers/ListingLocationsDashboardPage/AlertModalDelete";
import ReservationDetailsOwnerPage from "containers/Client/Reservation/reservationDetailsOwnerPage";

interface ReservationLoadingState {
  [key: string]: boolean;
}

interface ModernPendingReservationsContainerProps {
  className?: string;
}

const ModernPendingReservationsContainer: FC<ModernPendingReservationsContainerProps> = ({ className = "" }) => {
  const authContext = useContext(AuthContext);
  const authState = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingApprove, setIsLoadingApprove] = useState<ReservationLoadingState>({});
  const [isLoadingReject, setIsLoadingReject] = useState<ReservationLoadingState>({});
  const [hide, setHide] = useState<ReservationLoadingState>({});
  const [showReservationDetails, setShowReservationDetails] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Selectors
  const profilId = authContext?.user?.profil?.id || authState?.user?.profil?.id;
  const reservationState: any = useAppSelector((state) => state.featureReservation.reservations);
  const totalPages = useAppSelector((state) => state.featureReservation.totalPages);
  const totalCount = useAppSelector((state) => state.featureReservation.totalCount);

  // Filtrons les demandes réellement en attente
  const actualPendingReservations =
    reservationState?.filter((reservation: any) => reservation?.approuved === null) || [];

  useScrollToTop();

  // Fetch reservations
  useEffect(() => {
    const currentProfilId = authState?.user?.profil?.id || authContext?.user?.profil?.id;

    if (currentProfilId && authContext?.isAuthenticated) {
      dispatch(fetchPendingOwnerReservation({ id: currentProfilId, page: currentPage }));
    }
  }, [
    currentPage,
    authState?.user?.profil?.id,
    authContext?.user?.profil?.id,
    authContext?.isAuthenticated,
    dispatch,
  ]);

  // Handlers
  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const handleViewReservation = (reservation: any) => {
    setSelectedReservation(reservation);
    setShowReservationDetails(true);
  };

  const handleRefresh = () => {
    if (profilId && authContext?.isAuthenticated) {
      dispatch(fetchPendingOwnerReservation({ id: profilId, page: currentPage }));
    }
  };

  const handleApprove = (reservation: any) => {
    setSelectedReservation(reservation);
    setShowModal(true);
  };

  const handleReject = (reservation: any) => {
    setSelectedReservation(reservation);
    setShowDeleteModal(true);
  };

  const handleConfirmAction = async (reservation: any, response: boolean) => {
    if (setShowModal) {
      setShowModal(false);
    }
    if (setShowDeleteModal) {
      setShowDeleteModal(false);
    }

    if (response === true) {
      setIsLoadingApprove((prevState) => ({
        ...prevState,
        [reservation.id]: true,
      }));
    } else {
      setIsLoadingReject((prevState) => ({
        ...prevState,
        [reservation.id]: true,
      }));
    }

    const approuve = { approuved: response };
    const res = await dispatch(confirmReservation({ data: approuve, id: reservation.id }));
    const reservationDetail = reservationState[0];
    const senderId = reservationDetail?.Owner?.userId;
    const receiverId = reservationDetail?.Client?.userId;

    if (res.payload?.sendNotification !== undefined) {
      const action = res.payload.sendNotification ? "confirm_reservation" : "negate_reservation";

      socket.emit("send-notification", {
        senderId,
        receiverId,
        action,
        reservationId: reservation?.id,
        reservation,
      });
    }

    if (approuve.approuved === true) {
      navigate("/client/mes-reservations");
    }

    dispatch(fetchPendingOwnerReservation({ id: profilId, page: currentPage }));
    setHide((prevState) => ({
      ...prevState,
      [reservation.id]: true,
    }));

    if (response === true) {
      setIsLoadingApprove((prevState) => ({
        ...prevState,
        [reservation.id]: false,
      }));
    } else {
      setIsLoadingReject((prevState) => ({
        ...prevState,
        [reservation.id]: false,
      }));
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Modern Header */}
      <ModernPendingReservationsHeader totalCount={actualPendingReservations.length} onRefresh={handleRefresh} />

      {/* Reservations Grid */}
      {totalCount > 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
        >
          <AnimatePresence>
            {reservationState.map((reservation: any, index: number) => (
              <ModernReservationRequestCard
                key={reservation.id}
                reservation={reservation}
                onView={handleViewReservation}
                onApprove={handleApprove}
                onReject={handleReject}
                isLoadingApprove={isLoadingApprove[reservation.id]}
                isLoadingReject={isLoadingReject[reservation.id]}
                isHidden={hide[reservation.id]}
                isEventPassed={reservation?.isEventPassed === true}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      ) : (
        <div className="mt-8">{/* Empty state is handled in the header */}</div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex items-center justify-center mt-12"
        >
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </motion.div>
      )}

      {/* Modals */}
      <AlertModalDelete
        open={showModal}
        handleDelete={() => handleConfirmAction(selectedReservation, true)}
        handleClose={() => setShowModal(false)}
        message={`Êtes-vous sûr(e) de vouloir accepter cette réservation ?`}
        alertText="Acceptation de réservation"
      />

      <AlertModalDelete
        open={showDeleteModal}
        handleDelete={() => handleConfirmAction(selectedReservation, false)}
        handleClose={() => setShowDeleteModal(false)}
        message={`Êtes-vous sûr(e) de vouloir refuser cette réservation ?`}
        alertText="Refus de réservation"
      />

      {showReservationDetails && (
        <ReservationDetailsOwnerPage
          open={true}
          handleClose={() => setShowReservationDetails(false)}
          Data={selectedReservation}
        />
      )}
    </div>
  );
};

export default ModernPendingReservationsContainer;
